import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { DealsApi } from '../tokens/deals.token';
import {
  DealContractView,
  DealSignRequest,
  DealSignatureState,
  DealSummary,
  ListEnvelope,
} from '../models/financing-servicing.model';

/**
 * REST-only implementation of `DealsApi` (EPIC-4 / EM-500, Story 10 — EM-510).
 *
 * Calls the Story-3 `DealSigningResource` (by dealId) under `/services/embedded/api/v1/deals`.
 * `sign()` attaches the minted `X-SCA-Token` (sign_contract) — NEVER on `Authorization`. Rides
 * the Keycloak bearer (attached by `authInterceptor`). Rule 07 / Rule 10 — no Mock. Rule 09 —
 * the raw `HttpErrorResponse` propagates (a missing/invalid SCA token → 403 `scaRequired`).
 */
@Injectable()
export class DealsRestService implements DealsApi {
  private readonly http = inject(HttpClient);

  /**
   * EM-1816 — the party-scoped "contracts awaiting my signature" LIST, live at last.
   *
   * This used to return a structured-empty envelope with a `TODO(backend)`: the Story-3
   * `DealSigningResource` exposed deals BY ID only, so the Sign-Contracts sub-tab rendered a "coming
   * soon" notice above a numeric *Open contract by ID* box. That was survivable only while the
   * borrower could obtain an id from the contract-ready notification — and that notification
   * dead-ended on `/coming-soon`, so they could not. `GET /api/v1/deals` now returns the caller's own
   * pending-signature deals, resolved from the session party server-side.
   */
  list(): Observable<ListEnvelope<DealSummary>> {
    return this.http
      .get<DealSummary[]>(API_PATHS.DEALS)
      .pipe(map((items) => ({ available: true, items: items ?? [] })));
  }

  getContract(dealId: number): Observable<DealContractView> {
    return this.http.get<DealContractView>(`${API_PATHS.DEALS}/${dealId}/contract`);
  }

  getSignatures(dealId: number): Observable<DealSignatureState> {
    return this.http.get<DealSignatureState>(`${API_PATHS.DEALS}/${dealId}/signatures`);
  }

  sign(dealId: number, request: DealSignRequest, scaToken: string): Observable<DealSignatureState> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<DealSignatureState>(`${API_PATHS.DEALS}/${dealId}/sign`, request, { headers });
  }

  signAsEd(dealId: number, request: DealSignRequest, scaToken: string): Observable<DealSignatureState> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<DealSignatureState>(`${API_PATHS.DEALS}/${dealId}/sign/ed`, request, { headers });
  }

  signAsLender(dealId: number, request: DealSignRequest, scaToken: string): Observable<DealSignatureState> {
    const headers = new HttpHeaders({ 'X-SCA-Token': scaToken });
    return this.http.post<DealSignatureState>(`${API_PATHS.DEALS}/${dealId}/sign/lender`, request, { headers });
  }
}
